/**
 * ADR-0039 AI-1a — Typed contract for the command-first AI assistant.
 * ADR-0044 D3  — HELP response shape (read-only help article path).
 *
 * The SPA consumes this shape from POST /api/assist/resolve-intent.
 * IMPORTANT: this model is a *projection* of the server's resolve result -- it is NEVER
 * the LLM's raw output. The modal renders these values verbatim (Inv 5).
 *
 * The server derives:
 *   - naturalLanguageSummary from ActionKindRegistry.descriptionTemplate + params (ACTION)
 *                             OR from the article summary verbatim (HELP -- never LLM prose)
 *   - gates[]               from the rule-walker's entitlement resolve trace (empty for HELP/CLARIFY)
 *   - requiresStepUp        from the walker's step-up ceiling check (always false for HELP)
 *   - helpArticle           present when actionKind === 'HELP' (ADR-0044 D3)
 *   - suggestedArticles     present when actionKind === 'CLARIFY' with suggestions (ADR-0044 D3)
 *
 * The browser NEVER re-interprets these fields; it only renders them.
 */
import type { InsightsAskResponse } from '../api.service';

/** A single gate evaluated by the rule-walker during resolve. */
export interface IntentGate {
  /** e.g. "ENTITLEMENT", "VELOCITY", "STEP_UP", "KILL_SWITCH", "MAKER_CHECKER" */
  kind: string;
  /** Server's decision for this gate. */
  outcome: 'ALLOW' | 'DENY' | 'REQUIRES_APPROVAL';
  /** Optional human-readable detail (always server-built, never LLM prose). */
  detail?: string;
}

/**
 * The resolved intent response from POST /api/assist/resolve-intent.
 *
 * Fields mirror the ADR-0039 §D1 resolve-before-render contract.
 * `confidence` is retained for "did you mean?" disambiguation at low parse confidence
 * (below ≈ 0.75 the modal shows disambiguation instead of the 3 choices) — it is
 * NEVER used to authorize execution; authorization is re-done at execute time (Inv 3).
 */
export interface ResolveIntentResponse {
  /**
   * Registry action kind (e.g. "BOOK_TRANSFER", "GRANT_ENTITLEMENT", "GET_BALANCE"),
   * or "HELP" (ADR-0044 D3 -- a how-to / reference question was classified and resolved),
   * or "CLARIFY" (low-confidence or off-catalog).
   */
  actionKind: string;
  /** Structured parameters (server-schema-validated; empty for HELP and CLARIFY). */
  params: Record<string, unknown>;
  /**
   * Server-built natural-language summary from ActionKindRegistry descriptionTemplate (ACTION),
   * or the article summary verbatim from the catalog (HELP -- never LLM prose),
   * or the clarify reason (CLARIFY).
   * This IS the trusted display string (Inv 5 / ADR-0044 Inv 1).
   */
  naturalLanguageSummary: string;
  /**
   * Parse confidence from the local Ollama phi4 model [0.0–1.0].
   * Below 0.75 → show disambiguation / "did you mean?" state, never a confident "instant".
   * NEVER used to gate authorization (timing/confidence never gates authority — D5).
   */
  confidence: number;
  /** Ordered gate trace from the rule-walker. Empty for HELP and CLARIFY. */
  gates: IntentGate[];
  /**
   * True when the rule-walker's step-up ceiling fires for this action.
   * Always false for HELP and CLARIFY responses (no action, no gate).
   */
  requiresStepUp: boolean;
  /**
   * Server mirror of AssistToolCatalog.ToolEntry.isNavKind for catalog actions.
   * Only an explicit false starves OBO execution; responses that omit the field
   * (including mocked responses) are treated as nav-kind by default.
   */
  isNavKind?: boolean;
  /**
   * ADR-0044 D3: present when actionKind === 'HELP'.
   * The matched help article: id, title, summary (human-authored, verbatim), deepLink.
   * The SPA renders this with calm/read-only treatment -- no step-up, no form prefill,
   * no execute path. "Open in Help" button routes to the deepLink.
   */
  helpArticle?: HelpArticleRef | null;
  /**
   * ADR-0044 D3: present when actionKind === 'CLARIFY' and suggested articles are available.
   * Up to 3 suggested help articles for disambiguation. Rendered as chips/cards.
   * Empty array (never null) when no suggestions are available.
   */
  suggestedArticles?: HelpArticleRef[];
  /**
   * ADR-0044 GUIDE route: present when actionKind === 'GUIDE'.
   * The matched route's deterministic navigation + element steps, sourced verbatim from the
   * SPA-structure corpus (spa-guide.json). The SPA renders this with calm/read-only treatment:
   * a step list + a "Take me there" deep-link. There is NO execute path from a GUIDE response.
   */
  guide?: GuideRef | null;
  /**
   * ADR-0033/0039 J3: present when actionKind === 'ASK_INSIGHTS'.
   * This is the insights service AskResponse relayed by the BFF. The SPA renders it with
   * tb-nl-chart and never opens the execute modal for this read-only path.
   */
  insightsAnswer?: InsightsAskResponse | null;
  /**
   * Optional read payload returned by the server for read-only relay kinds.
   * edge-bff populates this for RM read kinds via ResolveIntentResponse.buildActionWithData().
   */
  data?: unknown | null;
}

/**
 * ADR-0044 GUIDE route -- a deterministic UI-navigation guide for one feature.
 *
 * Every field is sourced verbatim from the SPA-structure corpus -- the LLM only classified the
 * intent + mapped to a route. The SPA renders `steps` verbatim and uses `deepLink` (an in-app
 * relative route path, never a raw bearer) for the "Take me there" button.
 */
export interface GuideRef {
  /** The SPA route path (e.g. "admin/entitlements/grant"). */
  path: string;
  /** The route title verbatim from the corpus. */
  title: string;
  /** The menu path verbatim (e.g. "Payments > Beneficiaries"). May be null on bare routes. */
  navBreadcrumb?: string | null;
  /**
   * Ordered navigation + element steps, corpus-assembled (e.g. "Open Payments → Beneficiaries",
   * "Look for ..."). The SPA renders these verbatim -- they are NEVER LLM-invented click-paths.
   */
  steps: string[];
  /** Optional DOM-anchor metadata for GUIDE steps, sourced from the SPA guide corpus. */
  stepTargets?: Array<{ testid: string | null; label: string; role: string }> | null;
  /** In-app deep-link route path (e.g. "/admin/entitlements/grant"). Relative -- no raw bearer. */
  deepLink: string;
}

/**
 * ADR-0044 D3 -- A help article reference returned in the HELP response.
 *
 * The summary is human-authored repo content served by id -- NEVER LLM-generated prose.
 * The SPA must render summary verbatim; do not re-interpret or truncate it.
 */
export interface HelpArticleRef {
  /** Stable article id (e.g. "pay-wire"). */
  id: string;
  /** Article title from the catalog. */
  title: string;
  /**
   * Article summary -- verbatim from the help catalog (human-authored, never LLM prose).
   * This IS the trusted display string (ADR-0044 Inv 1).
   */
  summary: string;
  /** Deep-link URL path to the help article (e.g. "/help?article=pay-wire"). */
  deepLink: string;
}

// ── Entitlement demo beat action kinds ────────────────────────────────────────

/**
 * Beat 1 — "WHY": explain why a specific action was permitted/restricted/denied.
 *
 * Params the server populates:
 *   subjectId     -- canonical subject UUID (e.g. "a0000024-...-0024" for paula)
 *   subjectLabel  -- human display (e.g. "Paula (payroll_manager)")
 *   orgId         -- org UUID (e.g. Northwind "f1000000-0000-4000-8000-00000000f001")
 *   action        -- action kind probed (e.g. "INITIATE_PAYMENT")
 *   amount        -- decimal string (e.g. "42800.00") or null
 *   currency      -- ISO-4217 (e.g. "USD") or null
 *   highlightGate -- gate.kind that caused the restriction (server fills from hybrid-evaluate)
 *
 * The SPA navigates to /admin/entitlements/subject-authority with these as query params,
 * triggers evaluate, and highlights the named gate in red.
 */
export interface ExplainDecisionParams {
  subjectId: string;
  subjectLabel?: string | null;
  orgId?: string | null;
  action: string;
  amount?: string | null;
  currency?: string | null;
  /** Gate kind to highlight in red (e.g. "PAYROLL_CONTEXT_PURPOSE_RESTRICTED" or "REBAC_DENY"). */
  highlightGate?: string | null;
  /** ISO-20022 purpose code (e.g. "TAXS"); travels in the evaluate request context. */
  purposeCode?: string | null;
  /** Counterparty name; the payroll-purpose PBAC rule keys on purposeCode + a tax-ish counterparty. */
  counterpartyName?: string | null;
}

/**
 * Beat 2 — "DRAFT": prepare a time-boxed entitlement grant for human approval.
 *
 * Params the server populates:
 *   granteeId     -- subject UUID to grant to
 *   granteeLabel  -- display name (e.g. "Edda (external_auditor)")
 *   orgId         -- org UUID
 *   actions       -- array of action kinds to grant (e.g. ["VIEW_ACCOUNT","VIEW_REPORTS"])
 *   validUntil    -- ISO-8601 date (e.g. "2026-09-30") -- the time-box ceiling
 *   note          -- server-built rationale prose (e.g. "Temporary read access through quarter-end")
 */
export interface DraftGrantParams {
  granteeId: string;
  granteeLabel?: string | null;
  orgId?: string | null;
  actions: string[];
  validUntil?: string | null;
  note?: string | null;
}

/**
 * Beat 3 — "CASCADE / blast radius": assign a role and preview impact.
 *
 * Params the server populates:
 *   subjectId     -- subject UUID (e.g. vera's UUID)
 *   subjectLabel  -- display name (e.g. "Vera (controller)")
 *   orgId         -- org UUID
 *   roleId        -- role to assign (e.g. "TREASURER")
 *   roleLabel     -- human label (e.g. "Treasurer")
 */
export interface AssignRoleParams {
  subjectId: string;
  subjectLabel?: string | null;
  orgId?: string | null;
  roleId: string;
  roleLabel?: string | null;
}

/**
 * Returns true when the server could not parse the command (actionKind === 'CLARIFY',
 * confidence === 0). The command box renders this as a friendly inline "couldn't
 * understand — try rephrasing" message rather than opening the 3-way modal.
 *
 * The naturalLanguageSummary field carries the server's clarify reason in this case.
 */
export function isClarifyResponse(r: ResolveIntentResponse): boolean {
  return r.actionKind === 'CLARIFY';
}

/**
 * Returns true when the server classified the command as a how-to / reference question
 * and resolved it to a help article (ADR-0044 D3).
 *
 * The SPA renders the HELP response with calm/read-only treatment:
 * article title + summary + "Open in Help" button.
 * There is NO execute path from a HELP response.
 */
export function isHelpResponse(r: ResolveIntentResponse): boolean {
  return r.actionKind === 'HELP';
}

/**
 * Returns true when the server classified the command as a UI-navigation question
 * ("where is X / how do I get to Y") and resolved it to corpus-sourced guidance steps
 * (ADR-0044 GUIDE route).
 *
 * The SPA renders the GUIDE response with calm/read-only treatment:
 * a step list + a "Take me there" deep-link. There is NO execute path from a GUIDE response.
 */
export function isGuideResponse(r: ResolveIntentResponse): boolean {
  return r.actionKind === 'GUIDE' && !!r.guide;
}

/**
 * Returns true when the command resolved to a read-only insights answer.
 * The assistant renders this inline and never routes it to /api/assist/execute.
 */
export function isInsightsResponse(r: ResolveIntentResponse): boolean {
  return r.actionKind === 'ASK_INSIGHTS' && !!r.insightsAnswer;
}

/**
 * The three choices the user can make in the command modal (ADR-0039 §D1 §3).
 * CANCELLED is also emitted when the user presses Escape or clicks Cancel.
 */
export type AssistRmReadKind =
  | 'RM_VIEW_BOOK'
  | 'RM_VIEW_CLIENT_PORTFOLIO'
  | 'RM_VIEW_CLIENT_ACTIVITY'
  | 'RM_EXPLAIN_ENTITLEMENT_DENIAL';

const RM_READ_KINDS = new Set<string>([
  'RM_VIEW_BOOK',
  'RM_VIEW_CLIENT_PORTFOLIO',
  'RM_VIEW_CLIENT_ACTIVITY',
  'RM_EXPLAIN_ENTITLEMENT_DENIAL',
]);

export function isRmReadKind(actionKind: string): actionKind is AssistRmReadKind {
  return RM_READ_KINDS.has(actionKind.toUpperCase());
}

/**
 * Track 5 leg 3 — CUSTOMER read kinds whose payload the server already resolves and
 * relays in ResolveIntentResponse.data (same mechanism as the RM read kinds: edge-bff
 * runs the read on /api/assist/resolve-intent, entitlement-gated, via
 * RestAccountsReadClient). The SPA renders response.data inline and NEVER routes these
 * to the 3-way modal / OBO execute — a read must not hit the execute path.
 */
export type AssistAccountReadKind = 'VIEW_ACCOUNT' | 'GET_BALANCE' | 'GET_TRANSACTIONS';

const ACCOUNT_READ_KINDS = new Set<string>([
  'VIEW_ACCOUNT',
  'GET_BALANCE',
  'GET_TRANSACTIONS',
]);

export function isAccountReadKind(actionKind: string): actionKind is AssistAccountReadKind {
  return ACCOUNT_READ_KINDS.has(actionKind.toUpperCase());
}

export type AssistChoice = 'INSTANT' | 'DEMONSTRATE' | 'CANCELLED';

/**
 * Route classification for the INSTANT path (ADR-0039 money/entitlement prepare rule).
 *
 * MONEY_PREFILL     -- route to the pay form with pre-populated fields; the USER submits.
 *                      The chatbot does NOT auto-execute via OBO.
 * ENTITLEMENT_MODAL -- show a confirm modal for entitlement/admin actions. The user must
 *                      navigate to the admin UI and act there themselves.
 * DIRECT_ANSWER     -- read/low-stakes action; the assistant answers/handles directly
 *                      (calm AI-provenance treatment).
 * OBO_EXECUTE       -- (future / restricted) autonomous execute via agent-gateway OBO.
 *                      This path is NOT used for money or entitlement actions.
 *
 * Authority: AssistPrefillService MONEY_KINDS / ENTITLEMENT_KINDS sets.
 * NEVER determined by LLM confidence -- timing/confidence never gates authority (D5).
 */
export type AssistRouteKind =
  | 'MONEY_PREFILL'
  | 'ENTITLEMENT_MODAL'
  | 'DIRECT_ANSWER'
  | 'OBO_EXECUTE'
  | 'HELP';    // ADR-0044 D3: read-only help article response, no execute path

/** The user's complete modal decision -- emitted by the modal to the shell. */
export interface AssistDecision {
  response: ResolveIntentResponse;
  choice: AssistChoice;
  /** The route taken for the INSTANT path. Drives post-modal UX. */
  routeKind?: AssistRouteKind;
  /** Set only when choice === 'INSTANT' and requiresStepUp was true. */
  stepUpCompleted?: boolean;
  /**
   * Session-log thread support: set only when routeKind === 'OBO_EXECUTE' and the
   * gateway returned status='ok' — carries the outcomeRef so the durable turn record
   * can show the real downstream reference. Never present for money/entitlement kinds
   * (those never execute via the assistant — ADR-0039 prepare-not-execute).
   */
  executeResponse?: AssistExecuteResponse;
}

// ── AI-2b: Execute response contract ─────────────────────────────────────────

/**
 * Response from POST /api/assist/execute (ADR-0039 AI-2b).
 *
 * Status semantics (FROZEN — mirror AssistExecuteResponse.java):
 *   "ok"                    — gateway executed successfully; show success with outcomeRef.
 *   "pending_human_step_up" — step-up required; the action was NOT executed.
 *                             Route the user into the step-up ceremony, then retry execute.
 *   "declined"              — action denied (non-step-up: kill-switch, BOLA, entitlement ceiling).
 *                             The action was NOT executed; show an honest error.
 *   "error"                 — client/server error; the action was NOT executed.
 *   "unavailable"           — agent-gateway is not reachable (expected while enabled:false
 *                             until §3D is deployed); show a graceful "not available" message.
 *
 * SECURITY: success MUST ONLY be shown when status === 'ok'. For all other statuses, show
 * the appropriate non-success state. Never fabricate a success UI for non-ok statuses.
 */
export interface AssistExecuteResponse {
  /** "ok" | "pending_human_step_up" | "declined" | "error" | "unavailable" */
  status: 'ok' | 'pending_human_step_up' | 'declined' | 'error' | 'unavailable';
  /**
   * Present when status="ok": the downstream outcome reference
   * (e.g. a payment ID or transfer reference from the downstream service).
   */
  outcomeRef?: string | null;
  /**
   * Human-readable message; present for non-ok statuses.
   * For "pending_human_step_up": describes the step-up challenge.
   * For "declined"/"error"/"unavailable": describes the failure.
   */
  message?: string | null;
  /**
   * Present when status="pending_human_step_up": the challenge payload from the gateway,
   * opaque to the SPA (it drives the step-up ceremony service).
   */
  stepUpChallenge?: unknown;
}
